import React from 'react'
import { type AnalyticsEventName } from '@classy/campaign-page-blocks'
import { EVENT } from 'models/event'
import type { PageConfig } from 'models/pages'
import { buildMParticleGlobalProperties, useInternalAnalytics } from '.'
export interface AnalyticsTrackEventPayload {
  eventName: AnalyticsEventName
  value?: unknown
  trackingId?: string
  text?: string
  linkUrl?: string
  outbound?: boolean
}

export const useMParticleEventListener = (pageConfig: PageConfig, loggedInState: boolean) => {
  const { mParticle, consentSynced } = useInternalAnalytics()

  const eventHandler = React.useCallback(
    (e: Event) => {
      const { detail } = e as CustomEvent<AnalyticsTrackEventPayload>
      if (!mParticle || !consentSynced || !detail) return

      const globalProperties = buildMParticleGlobalProperties(pageConfig, mParticle, loggedInState)

      /**
       * valueAttr: normalize detail.value to a string
       * (stringify objects, coerce primitives) for mParticle.
       */
      const valueAttr =
        typeof detail.value === 'object' && detail.value !== null
          ? JSON.stringify(detail.value)
          : String(detail.value)

      /**
       * Build optional attrs from CustomEvent detail and include only fields the caller provided.
       * - Filter out null/undefined so we don't send empty keys to analytics
       * - Keep boolean false and numeric 0 (use v != null, not a truthy check)
       * - Trim linkUrl; drop empty strings by mapping to undefined
       */
      const attrs = Object.fromEntries(
        Object.entries({
          value: detail.value != null ? valueAttr : undefined,
          tracking_id: detail.trackingId,
          text: detail.text?.trim() || undefined,
          linkUrl: detail.linkUrl?.trim() || undefined,
          outbound: detail.outbound,
        }).filter(([, v]) => v != null),
      )

      mParticle.logEvent(
        detail.eventName,
        mParticle.EventType.Other,
        {
          ...globalProperties,
          ...attrs,
        },
        {
          'GA4.Title': document.title,
          'GA4.Location': document.location.href,
          'GA4.Referrer': document.referrer,
        },
      )
    },
    [mParticle, consentSynced, pageConfig, loggedInState],
  )

  // * Listen for analytics track events dispatched via EVENT.ANALYTICS_TRACK_EVENT
  React.useEffect(() => {
    document.addEventListener(EVENT.ANALYTICS_TRACK_EVENT, eventHandler)

    return () => {
      document.removeEventListener(EVENT.ANALYTICS_TRACK_EVENT, eventHandler)
    }
  }, [eventHandler])
}
